import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import "./App.css";

import Layout from "./components/Layout/Layout";
import { getToken } from "./utils/auth";
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/common/ErrorBoundary';
import { UserProvider, useUser } from './context/UserContext';
import { NotificationProvider } from './context/NotificationContext';

// Lazy load all page components for better performance
const Login = lazy(() => import("./components/Login/Login"));
const Register = lazy(() => import("./components/Register/Register"));
const CreateCompany = lazy(() => import("./components/CreateCompany/CreateCompany"));
const UserManagement = lazy(() => import("./components/UserManagement/UserManagement"));
const StudentList = lazy(() => import("./components/StudentList/StudentList"));
const SponsorList = lazy(() => import("./components/SponsorList/SponsorList"));
const SponsorStudentMapping = lazy(() => import("./components/SponsorStudentMapping/SponsorStudentMapping"));
const SchoolConfiguration = lazy(() => import('./components/SchoolConfiguration/SchoolConfiguration'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const GradingSchemePage = lazy(() => import('./components/GradingScheme/GradingSchemePage'));
const TeacherList = lazy(() => import('./components/TeacherList/TeacherList'));
const AttendanceAnalysisReport = lazy(() => import('./components/Analytics/AttendanceAnalysisReport')); // New Analytics
const MarksEntry = lazy(() => import('./components/MarksEntry/MarksEntry'));
const ApprovalsList = lazy(() => import('./components/MarksEntry/ApprovalsList'));
const MarksReview = lazy(() => import('./components/MarksEntry/MarksReview'));
const TeacherCorrections = lazy(() => import('./components/MarksEntry/TeacherCorrections'));
const AttendanceEntry = lazy(() => import('./components/AttendanceEntry/AttendanceEntry'));
const AttendanceReports = lazy(() => import('./components/AttendanceEntry/AttendanceReports'));
const ReportCardGeneration = lazy(() => import('./components/ReportCardGeneration/ReportCardGeneration'));
const MarksManagement = lazy(() => import('./components/MarksManagement/MarksManagement'));
// Student Portal Components
const StudentDashboard = lazy(() => import('./components/StudentPortal/StudentDashboard'));
const StudentProfile = lazy(() => import('./components/StudentPortal/StudentProfile'));
const StudentAttendance = lazy(() => import('./components/StudentPortal/StudentAttendance'));
const StudentReportCards = lazy(() => import('./components/StudentPortal/StudentReportCards'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const ProgressiveReportCards = lazy(() => import('./components/ReportCards/ProgressiveReportCards'));

// --- NEW COMPONENT ---
const UniversalImport = lazy(() => import('./components/Import/UniversalImport'));
const PendingSponsorships = lazy(() => import('./components/PendingSponsorships/PendingSponsorships'));
const TCDonations = lazy(() => import('./components/TCDonations/TCDonations'));
const SponsorshipAmounts = lazy(() => import('./components/SponsorshipAmounts/SponsorshipAmounts'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="page-loading">
    <div className="loading-spinner"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useUser();

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// 1. Create a Wrapper that holds the persistent Layout
// The <Outlet /> renders whatever child route is currently active
const LayoutWrapper = () => {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

// 2. Updated PlaceholderPage (Removed the internal <Layout> wrapper)
const PlaceholderPage = ({ title }) => (
  <div className="placeholder-container">
    <h1 className="placeholder-title">{title}</h1>
    <div className="placeholder-card">
      <p className="placeholder-text">The <strong>{title}</strong> module is coming soon.</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <UserProvider>
        <NotificationProvider>
          <Toaster position="top-right" reverseOrder={false} />
          <Router>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes (No Sidebar) */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/create-company" element={<CreateCompany />} />

                {/* Protected Routes (Wrapped in Layout) */}
                {/* This Route groups all pages that share the Sidebar */}
                <Route element={<ProtectedRoute><LayoutWrapper /></ProtectedRoute>}>

                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/analytics" element={<PlaceholderPage title="Apps Analytics" />} />
                  <Route path="/analytics/attendance/:studentId" element={<AttendanceAnalysisReport />} />
                  <Route path="/manage-app" element={<PlaceholderPage title="Manage App" />} />

                  {/* 
                     IMPORTANT: Ensure these components do NOT have <Layout> inside them 
                     or you will see two Sidebars.
                  */}
                  <Route path="/user-management" element={<UserManagement />} />
                  <Route path="/students" element={<StudentList />} />
                  <Route path="/teachers" element={<TeacherList />} />
                  <Route path="/marks-management" element={<MarksManagement />} />
                  <Route path="/marks-entry" element={<MarksEntry />} />
                  <Route path="/marks-entry/student/:studentId" element={<MarksEntry />} />
                  <Route path="/approvals" element={<ApprovalsList />} />
                  <Route path="/attendance-entry" element={<AttendanceEntry />} />
                  <Route path="/attendance-reports" element={<AttendanceReports />} />
                  <Route path="/attendance-dashboard" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/attendance-analytics" element={<Navigate to="/dashboard" replace />} />

                  <Route path="/sponsors" element={<SponsorList />} />
                  <Route path="/pending-sponsorships" element={<PendingSponsorships />} />
                  <Route path="/tc-donations" element={<TCDonations />} />
                  <Route path="/sponsor-mapping" element={<SponsorStudentMapping />} />
                  <Route path="/school-configuration" element={<SchoolConfiguration />} />
                  <Route path="/sponsorship-amounts" element={<SponsorshipAmounts />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/grading-scheme" element={<GradingSchemePage />} />
                  <Route path="/marks/review/:studentId" element={<MarksReview />} />
                  <Route path="/corrections" element={<TeacherCorrections />} />
                  <Route path="/report-cards" element={<ReportCardGeneration />} />
                  <Route path="/progressive-report-cards" element={<ProgressiveReportCards />} />

                  {/* --- NEW ROUTE for Universal Import --- */}
                  <Route path="/manual-import" element={<UniversalImport />} />

                  {/* Student Portal Routes */}
                  <Route path="/student/dashboard" element={<StudentDashboard />} />
                  <Route path="/student/profile" element={<StudentProfile />} />
                  <Route path="/student/attendance" element={<StudentAttendance />} />
                  <Route path="/student/report-cards" element={<StudentReportCards />} />

                </Route>

                {/* Fallback Routes */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
            </Suspense>
          </Router>
        </NotificationProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

export default App;